import { Component } from '@angular/core';
import { ApiImpact, ApiUser, ImpactsApi } from '@tytapp/api';
import { AppConfig, PageComponent, PagedArray, PageService, Transfer } from '@tytapp/common';
import { COMMUNITY_C2A, DONATION_C2A, MEMBERSHIP_C2A } from '@tytapp/common/c2a';
import { environment } from '@tytapp/environment';
import { UserService } from '@tytapp/user';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Location } from '@angular/common';

@Component({
    selector: 'tyt-company-impact-page',
    templateUrl: './company-impact-page.component.html',
    styleUrls: ['./company-impact-page.component.scss']
})
export class CompanyImpactPageComponent extends PageComponent {
    @Transfer()
    impacts: PagedArray<ApiImpact>;

    @Transfer()
    impactsFirstSection: ApiImpact[];

    @Transfer()
    impactsSecondSection: ApiImpact[];

    @Transfer()
    user: ApiUser;

    @Transfer()
    pageVariant: 'default' | 'videoHeroLayout' = 'default';

    @Transfer()
    otcUrl: string;

    @Transfer()
    offerSetID: string;

    @Transfer()
    offerSetType: 'donation' | 'membership';

    constructor(
        pageService: PageService,
        private impactsApi: ImpactsApi,
        private userService: UserService,
        private appConfig: AppConfig,
        private route: ActivatedRoute,
        private domSanitizer: DomSanitizer,
        private location: Location
    ) {
        super(pageService);
    }

    get purchaseUrl() {
        return environment.urls.purchase;
    }

    /**
     * If the offerSetType is 'donation' we should always show this.
     * If the offerSetType is 'membership', only show it if the user is not a member already
     */
    get showEmbeddedOffers(): boolean {
        if (this.offerSetType === 'membership')
            return this.offerSetID && (!this.user || !this.user.membership || !this.user.membership.active);
        else if (this.offerSetType === 'donation')
            return !!this.offerSetID;
    }

    public heroVideoURL: string;
    public heroVideoTitle: string;
    public heroVideoDescription: string;
    public isHeroVideoYouTube: boolean;
    public heroVideoYouTubeEmbedUrl: SafeResourceUrl;

    async init() {
        this.subscribe(this.route.queryParamMap, (async (params: ParamMap) => {
            console.log(`Received query params...`);
            if (!params.has('o') && !params.has('v')) {
                await this.appConfig.appStatusReady;

                if (await this.shell.hasFeature('apps.web.enable_impacts_split_test')) {
                    console.log(`Selecting Impacts variant...`);
                    try {
                        let config: ({ o: string, v: number })[] =
                            JSON.parse(this.appConfig.appStatus.settings['impacts_split_test_config'])
                        ;

                        if (config) {
                            let selectedConfig = config[Math.floor(Math.random() * config.length)];
                            let v = [1,2].includes(selectedConfig.v) ? selectedConfig.v : 1;
                            console.log(`Selected variant: ${JSON.stringify(selectedConfig)}`);
                            this.location.replaceState(`/impacts`, `?v=${v}&o=${selectedConfig.o}`);
                            this.setOfferSetID(selectedConfig.o);
                            this.checkLayoutVersion(`${v}`);
                        }
                    } catch (e) {
                        console.error('Caught error while parsing the server-side Impacts split test configuration:')
                        console.error(e);
                    }
                }
            } else {
                this.setOfferSetID(params.get('o'));
                this.checkLayoutVersion(params.get('v'));
            }
        }));
        this.subscribe(this.userService.userChanged, user => this.user = user);

        if (!this.hasServerState) {
            this.subscribe(this.impactsApi.getImpacts(), (data) => {
                this.impacts = data;
                if (this.impacts.length > 2) {
                    this.impactsFirstSection = this.impacts.slice(0, 2);
                    this.impactsSecondSection = this.impacts.slice(2);
                } else {
                    this.impactsFirstSection = this.impacts.slice();
                    this.impactsSecondSection = [];
                }
            });

            if (await this.appConfig.featureEnabled('apps.web.enable_otc_c2a')) {
                this.otcUrl = `${environment.urls.accounts}/settings/one-time-contribution`;
            } else {
                this.otcUrl = 'https://tyt.com/go';
            }
        }
    }

    private async setOfferSetID(offerSetId: string): Promise<void> {
        if (offerSetId) {
            console.log(`[Impacts]: Override Offer Set: ${offerSetId} `);
            this.offerSetID = offerSetId;
        } else {
            await this.appConfig.appStatusReady;
            if (this.appConfig.appStatus && this.appConfig.appStatus.settings && this.appConfig.appStatus.settings['impacts_page_offer_set']) {
                this.offerSetID = this.appConfig.appStatus.settings['impacts_page_offer_set'];
            }
        }
        this.offerSetType = this.appConfig.appStatus.settings['impacts_page_offer_set_type'] || 'donation';
    }

    showLearnMore: boolean = true;

    private async checkLayoutVersion(layoutVersion: string) {
        await this.appConfig.appStatusReady;
        if (layoutVersion) {
            this.pageVariant = layoutVersion == '2' ? 'videoHeroLayout' : 'default';
        } else {
            this.pageVariant = this.appConfig.appStatus.settings['impacts_layout_variant'] == '2' ? 'videoHeroLayout' : 'default';
        }

        if (this.pageVariant === 'videoHeroLayout') {
            this.showLearnMore = false;
            this.heroVideoURL = this.appConfig.appStatus.settings['impacts_hero_video'];
            this.heroVideoTitle = this.appConfig.appStatus.settings['impacts_hero_title'];
            this.heroVideoDescription = this.appConfig.appStatus.settings['impacts_hero_description'];
            this.heroVideoYouTubeEmbedUrl = undefined;

            this.isHeroVideoYouTube = this.heroVideoURL && this.heroVideoURL.includes('youtube.com/watch');
            if (this.isHeroVideoYouTube) {
                // https://www.youtube.com/embed/769Z4tqF8g4

                let match = this.heroVideoURL.match(/watch\?v=([^&]+)/);
                if (match)
                    this.heroVideoYouTubeEmbedUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${match[1]}`);
            }
        } else {
            this.showLearnMore = true;
        }

    }

    get c2a() {
        MEMBERSHIP_C2A.url = this.purchaseUrl;
        DONATION_C2A.url = this.otcUrl;

        if (!this.user)
            return COMMUNITY_C2A;
        if (!this.user.membership || !this.user.membership.active)
            return MEMBERSHIP_C2A;
        return DONATION_C2A;
    }
}
